import { ChevronLeft, ChevronRight, X } from 'lucide-react'
import { useCallback, useEffect, useRef, useState } from 'react'
import type { GalleryPhoto } from '@/lib/portal-types'

/**
 * Galeria de fotos com ampliacao, usada por noticias e por eventos.
 *
 * A ampliacao usa `<dialog>` nativo em vez de uma div posicionada: o navegador
 * ja entrega o foco preso dentro do modal, o fechamento por Esc e o fundo
 * inerte. Reimplementar isso a mao e exatamente onde modal caseiro deixa quem
 * navega por teclado presor atras da foto.
 */
export function GaleriaDeFotos({
  fotos,
  titulo = 'Fotos',
}: {
  fotos: GalleryPhoto[]
  titulo?: string
}) {
  const dialogRef = useRef<HTMLDialogElement>(null)
  const [aberta, setAberta] = useState<number | null>(null)

  const fechar = useCallback(() => {
    dialogRef.current?.close()
  }, [])

  const abrir = useCallback((indice: number) => {
    setAberta(indice)
    dialogRef.current?.showModal()
  }, [])

  const navegar = useCallback(
    (passo: number) => {
      setAberta((atual) => {
        if (atual === null) return atual
        // Circular: na ultima foto, avancar volta para a primeira. Quem chegou
        // ao fim de vinte fotos nao quer descobrir que o botao morreu.
        return (atual + passo + fotos.length) % fotos.length
      })
    },
    [fotos.length],
  )

  useEffect(() => {
    const dialog = dialogRef.current
    if (!dialog) return

    // `close` cobre Esc, clique no backdrop e o botao de fechar: um unico lugar
    // para limpar o estado.
    const aoFechar = () => setAberta(null)
    dialog.addEventListener('close', aoFechar)

    const aoTeclar = (evento: KeyboardEvent) => {
      if (evento.key === 'ArrowRight') navegar(1)
      if (evento.key === 'ArrowLeft') navegar(-1)
    }
    dialog.addEventListener('keydown', aoTeclar)

    return () => {
      dialog.removeEventListener('close', aoFechar)
      dialog.removeEventListener('keydown', aoTeclar)
    }
  }, [navegar])

  if (fotos.length === 0) return null

  const atual = aberta === null ? null : fotos[aberta]

  return (
    <section className="mt-12" aria-labelledby="titulo-galeria">
      <h2 id="titulo-galeria" className="font-serif text-2xl font-semibold">
        {titulo}
      </h2>
      <p className="mt-1 text-sm text-suave">
        {fotos.length === 1 ? '1 foto' : `${fotos.length} fotos`} · toque para ampliar
      </p>

      <ul className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4">
        {fotos.map((foto, indice) => (
          <li key={foto.id}>
            <button
              type="button"
              onClick={() => abrir(indice)}
              className="group block w-full overflow-hidden rounded-lg border border-borda focus-visible:outline-3"
            >
              <img
                src={foto.thumb}
                alt={foto.alt ?? foto.caption ?? `Foto ${indice + 1}`}
                loading="lazy"
                /* Miniatura recortada em quadrado para a grade nao ficar
                   irregular; a foto inteira aparece na ampliacao. */
                className="aspect-square w-full object-cover transition-transform group-hover:scale-105"
              />
              {foto.caption && (
                <span className="block p-2 text-left text-xs text-suave">{foto.caption}</span>
              )}
            </button>
          </li>
        ))}
      </ul>

      <dialog
        ref={dialogRef}
        /* backdrop:bg-black/80 escurece o fundo sem precisar de div propria. */
        className="m-auto max-h-[92vh] max-w-[95vw] rounded-lg bg-cartao p-0 backdrop:bg-black/80"
        aria-label="Foto ampliada"
      >
        {atual && (
          <div className="flex max-h-[92vh] flex-col">
            <div className="flex items-center justify-between gap-2 border-b border-borda p-2">
              <p className="px-2 text-sm text-suave">
                {(aberta ?? 0) + 1} de {fotos.length}
              </p>
              <button
                type="button"
                onClick={fechar}
                className="flex size-10 items-center justify-center rounded-md hover:bg-afundado focus-visible:outline-3"
              >
                <X aria-hidden="true" />
                <span className="sr-only">Fechar a foto ampliada</span>
              </button>
            </div>

            <div className="relative flex min-h-0 flex-1 items-center justify-center bg-afundado">
              <img
                src={atual.large}
                alt={atual.alt ?? atual.caption ?? 'Foto ampliada'}
                className="max-h-[70vh] max-w-full object-contain"
              />

              {fotos.length > 1 && (
                <>
                  <button
                    type="button"
                    onClick={() => navegar(-1)}
                    className="absolute top-1/2 left-2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-cartao/90 focus-visible:outline-3"
                  >
                    <ChevronLeft aria-hidden="true" />
                    <span className="sr-only">Foto anterior</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => navegar(1)}
                    className="absolute top-1/2 right-2 flex size-11 -translate-y-1/2 items-center justify-center rounded-full bg-cartao/90 focus-visible:outline-3"
                  >
                    <ChevronRight aria-hidden="true" />
                    <span className="sr-only">Proxima foto</span>
                  </button>
                </>
              )}
            </div>

            {atual.caption && (
              <p className="border-t border-borda p-3 text-sm">{atual.caption}</p>
            )}
          </div>
        )}
      </dialog>
    </section>
  )
}
